import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { axInst } from "../../../Components/Login/Service";
import { Loader } from "rsuite";
import WaterMark from "../../../Components/WaterMark/WaterMark";

const HARDCODED_MONTHS = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];

const psfSalesApi = async (body) => {
  try {
    const res = await axInst.post("dealer-review/psf_sales", body);
    return res.data;
  } catch (error) {
    console.error("Error fetching stock data:", error.response || error);
    throw error;
  }
};

const PsfSales = () => {
  const { data, submitted } = useSelector((e) => e.filtersDealerReview);

  const {
    data: psfSalesData,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["psfSales_data", submitted],
    queryFn: () => psfSalesApi(data),
    cacheTime: 0,
    staleTime: 0,
  });

  // Columns are fixed and don't need to change unless months change
  const columns = useMemo(
    () => [
      { accessorKey: "category", header: "" },
      ...HARDCODED_MONTHS.map((month) => ({
        accessorKey: month,
        header: month,
      })),
    ],
    []
  );

  // Table data depends only on psfSalesData
  const tableData = useMemo(() => {
    if (!psfSalesData) return [];

    return psfSalesData.map((item) => {
      const key = item.id;
      const monthData = item[key] || {};
      const isPercent = key.includes("%");

      return {
        category: key,
        ...HARDCODED_MONTHS.reduce((acc, month) => {
          const value = monthData[month];
          acc[month] =
            value != null && value !== "-"
              ? isPercent
                ? `${value}%`
                : value
              : "-";
          return acc;
        }, {}),
      };
    });
  }, [psfSalesData]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isFetching)
    return (
      <div className="grid items-center justify-center w-full h-full">
        <Loader />
      </div>
    );

  if (isError || !psfSalesData || psfSalesData.length === 0)
    return <div>No Data Available</div>;

  return (
    <div className="relative w-full shadow-lg rounded-md">
      <WaterMark />
      <p className="font-semibold p-2 rounded-md bg-white">PSF Sales</p>

      <div className="w-full drilltable rounded-md">
        <table className="w-full min-w-[410px]  table-auto border-collapse">
          <thead>
            {table.getHeaderGroups().map((headerGroup, index) => (
              <tr key={`header-group-${index}`}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="p-2 bg-[#28536F] text-white text-sm font-semibold border border-[#B9B9B9] whitespace-nowrap"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={`border border-[#B9B9B9] ${
                  row.index % 2 === 1 ? "bg-[#eef0f5]" : "bg-white"
                }`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`text-xs py-2 border border-[#B9B9B9]  ${
                      cell.column.id === "category"
                        ? "bg-[#D4E1EC]  font-semibold text-left px-1 whitespace-nowrap"
                        : "text-center px-1"
                    }`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PsfSales;
